import {
  validationTypes,
  validationTypeMeta,
  VALIDATION_VALUE_KIND,
} from "./whatsappFlowCatalogs";

const KNOWN_TYPES = new Set(validationTypes);

/** @typedef  {{ type: string, value?: string }} ValidationRule */

const parseSegment = (segment) => {
  const s = String(segment).trim();
  if (!s) return null;
  const i = s.indexOf(":");
  if (i === -1) {
    return { type: s, value: undefined };
  }
  const type = s.slice(0, i).trim();
  const value = s.slice(i + 1).trim();
  return { type, value: value === "" ? undefined : value };
};

/**
 * @param {string | null | undefined} input
 * @returns {ValidationRule[]}
 */
export const parseValidationString = (input) => {
  if (input == null || String(input).trim() === "") return [];
  return String(input)
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean)
    .map(parseSegment)
    .filter(Boolean);
};

/** @returns {{ amountThreshold: string, fpdThreshold: string }} */
export const splitFraudValidationValue = (value) => {
  const [amountThreshold = "", fpdThreshold = ""] = String(value ?? "").split("|");
  return { amountThreshold, fpdThreshold };
};

/** @returns {{ field: string, message: string } | null} */
export const getFraudValidationError = (rule) => {
  const rawValue = String(rule?.value ?? "");
  const { amountThreshold, fpdThreshold } = splitFraudValidationValue(rule?.value);
  if (!amountThreshold.trim()) {
    return { field: "monto", message: "Ingresa el umbral de monto en COP." };
  }
  const amount = Number(amountThreshold);
  if (!/^\d+$/.test(amountThreshold) || !Number.isFinite(amount) || amount < 1) {
    return { field: "monto", message: "El monto debe ser un número entero de COP mayor que cero." };
  }
  if (!fpdThreshold.trim()) {
    return { field: "FPD", message: "Ingresa el umbral FPD." };
  }
  const fpd = Number(fpdThreshold);
  const validFpdFormat = /^(?:\d+(?:\.\d*)?|\.\d+)$/.test(fpdThreshold);
  if (
    rawValue.split("|").length !== 2 ||
    !validFpdFormat ||
    !Number.isFinite(fpd) ||
    fpd < 0 ||
    fpd > 1
  ) {
    return { field: "FPD", message: "El umbral FPD debe ser un número entre 0 y 1." };
  }
  return null;
};

/** @returns {string | null} */
export const getFlowFraudValidationError = (nodes) => {
  for (const node of Array.isArray(nodes) ? nodes : []) {
    const stepName = node.data?.name || node.id || "(sin nombre)";
    const fraudRules = (node.data?.validationRules || []).filter(
      (rule) => rule?.type === "FRAUD_VALIDATION"
    );
    // El agente guarda un solo resultado de fraude por paso: una segunda regla nunca se evaluaría.
    if (fraudRules.length > 1) {
      return `Paso “${stepName}”: solo se permite una regla de fraude por paso.`;
    }
    const error = fraudRules.length === 1 ? getFraudValidationError(fraudRules[0]) : null;
    if (error) {
      return `Paso “${stepName}”, regla de fraude, campo ${error.field}: ${error.message}`;
    }
  }
  return null;
};

/**
 * AGE_RANGE se configura como `min-max` (años, ambos incluidos). El agente usa 18-69 si el valor
 * llega vacío o mal escrito, así que el editor no debe dejar guardarlo así.
 * @returns {string | null}
 */
export const getAgeRangeValidationError = (rule) => {
  const raw = String(rule?.value ?? "").trim();
  if (!raw) {
    return "Ingresa el rango de edad, por ejemplo 18-69.";
  }
  const match = /^(\d{1,3})\s*-\s*(\d{1,3})$/.exec(raw);
  if (!match) {
    return "El rango de edad debe tener el formato mínimo-máximo, por ejemplo 18-69.";
  }
  if (Number(match[1]) > Number(match[2])) {
    return "En el rango de edad, el mínimo no puede ser mayor que el máximo.";
  }
  return null;
};

/** @returns {string | null} */
export const getFlowAgeRangeValidationError = (nodes) => {
  for (const node of Array.isArray(nodes) ? nodes : []) {
    const stepName = node.data?.name || node.id || "(sin nombre)";
    const ageRules = (node.data?.validationRules || []).filter((rule) => rule?.type === "AGE_RANGE");
    for (const rule of ageRules) {
      const error = getAgeRangeValidationError(rule);
      if (error) {
        return `Paso “${stepName}”, regla de rango de edad: ${error}`;
      }
    }
  }
  return null;
};

/**
 * @param {ValidationRule[]} rules
 * @returns {string}
 */
export const serializeValidationRules = (rules) => {
  if (!Array.isArray(rules) || rules.length === 0) return "";
  const parts = [];
  for (const r of rules) {
    if (!r || !r.type) continue;
    const t = String(r.type).trim();
    if (!KNOWN_TYPES.has(t)) continue;
    const meta = validationTypeMeta[t];
    const raw = r.value != null ? String(r.value) : "";
    const v = raw.trim();
    const needsValue =
      meta &&
      meta.valueKind !== VALIDATION_VALUE_KIND.NONE &&
      meta.valueKind !== "none";
    if (needsValue && !v) continue;
    if (needsValue) {
      parts.push(`${t}:${raw.trim()}`);
    } else {
      parts.push(t);
    }
  }
  return parts.join(",");
};

/**
 * @param {ValidationRule[]} rules
 * @returns {string[]}
 */
export const getOptionsTokensFromRules = (rules) => {
  const opt = Array.isArray(rules) ? rules.find((r) => r?.type === "OPTIONS") : null;
  if (!opt?.value) return [];
  return String(opt.value)
    .split("%")
    .map((x) => x.trim())
    .filter(Boolean);
};

/**
 * Construye `validationRules` desde datos de paso (nuevo o legado).
 * @param {object} data
 * @returns {ValidationRule[]}
 */
export const computeValidationRulesFromStepData = (data) => {
  if (!data || typeof data !== "object") return [];
  if (Array.isArray(data.validationRules) && data.validationRules.length > 0) {
    return data.validationRules
      .map((r) =>
        r && typeof r === "object" && r.type
          ? { type: String(r.type).trim(), value: r.value != null ? String(r.value) : undefined }
          : null
      )
      .filter(Boolean);
  }
  if (typeof data.validations === "string") {
    return parseValidationString(data.validations);
  }
  if (Array.isArray(data.validations)) {
    return data.validations
      .map((x) => {
        if (typeof x === "string") {
          return parseSegment(x);
        }
        if (x && typeof x === "object" && x.type) {
          return {
            type: String(x.type).trim(),
            value: x.value != null ? String(x.value) : undefined,
          };
        }
        return null;
      })
      .filter(Boolean);
  }
  return [];
};
